import React, { useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { typeTitle, TYPE, STATUS, PRIO } from "../../design/ui";
import { EmptyState, PRIO_KIND, StatusChip, TASK_STATUS_KIND } from "../mui";
import TaskDetailPanel from "./TaskDetailPanel";
import { MdClose } from "react-icons/md";

/**
 * 工項抽屜 —— 右側滑入，內嵌完整的工項詳情。
 *
 * 從簽核中心的審核抽屜抽出來的共用版本：看板、清單類頁面點一筆工項時，
 * 使用者要的是「看內容」而不是「跳到另一頁再跳回來」。
 *
 * 用 MUI Drawer：它本身就 portal 到 document.body（頁面外層的 .pg 有
 * `animation: fadeUp .34s both`，留下的 transform 會讓頁面內的 position:fixed
 * 錨在內容區而不是視窗，所以一定要 portal），也內建 ESC 關閉、點遮罩關閉、鎖背景捲動。
 * 寬度用百分比上限而不是 vw：vw 不扣掉捲軸寬度，有縱向捲軸時會比可視區還寬。
 */
function TaskDrawer({ open, taskId: taskIdProp, task, projectName: projectNameProp, onClose, onChanged, onOpenTask }) {
  /*
   * 呼叫端關閉時通常會把 task 一起清成 null，但 Drawer 還有一段滑出動畫；
   * 動畫期間沿用最後一次打開時的內容，免得抽屜在滑走途中閃成「找不到對應的工項」。
   */
  const last = useRef({ taskId: taskIdProp, task, projectName: projectNameProp });
  if (open) last.current = { taskId: taskIdProp, task, projectName: projectNameProp };
  const { taskId, projectName } = last.current;
  const t = last.current.task || {};
  const typeKey = t.type && TYPE[t.type] ? t.type : "task";
  const statusKey = t.status && STATUS[t.status] ? t.status : null;
  const prioKey = t.prio && PRIO[t.prio] ? t.prio : null;

  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 720, maxWidth: "96%", display: "flex", flexDirection: "column" } } }}
    >
      {/* 標題列 */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 2.5, py: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            {t.code && (
              <Typography
                component="code"
                sx={{ fontFamily: "var(--font-mono)", fontSize: "var(--t-code)", color: "text.secondary", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
              >
                {t.code}
              </Typography>
            )}
            <StatusChip kind="type" title={typeTitle(typeKey)}>
              {(TYPE[typeKey] || TYPE.task).label}
            </StatusChip>
            {statusKey && <StatusChip kind={TASK_STATUS_KIND[statusKey]}>{STATUS[statusKey].label}</StatusChip>}
            {prioKey && <StatusChip kind={PRIO_KIND[prioKey]}>{PRIO[prioKey].label}</StatusChip>}
            {projectName && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {projectName}
              </Typography>
            )}
          </Stack>
          {/* 標題用 section 字級：大字模式下才不會比「點開它的那張卡片」的標題還小 */}
          {t.name && (
            <Typography variant="h3" component="h2" sx={{ mt: 1 }}>
              {t.name}
            </Typography>
          )}
        </Box>
        {/* 關閉鈕一定要有文字、命中區要 ≥ 44px（硬規則 3）：純圖示叉叉讀螢幕只會聽到沒有名字的按鈕 */}
        <Button type="button" variant="text" onClick={onClose} startIcon={<MdClose aria-hidden="true" />} sx={{ flexShrink: 0 }}>
          關閉
        </Button>
      </Box>

      {/* 內容區 */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        {taskId == null ? (
          <EmptyState title="找不到對應的工項" text="請重新整理頁面後再試。" />
        ) : (
          <TaskDetailPanel
            key={`task-${taskId}`}
            taskId={taskId}
            taskCode={t.code}
            projectName={projectName}
            embedded
            onChanged={onChanged}
            onOpenTask={onOpenTask}
          />
        )}
      </Box>
    </Drawer>
  );
}

TaskDrawer.propTypes = {
  open: PropTypes.bool,
  taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** 標題列要顯示的摘要（代碼 / 名稱 / 型別 / 狀態 / 優先級），內容仍由 TaskDetailPanel 自行載入 */
  task: PropTypes.object,
  projectName: PropTypes.string,
  onClose: PropTypes.func,
  onChanged: PropTypes.func,
  onOpenTask: PropTypes.func,
};

TaskDrawer.defaultProps = {
  open: false,
  taskId: null,
  task: null,
  projectName: "",
  onClose: () => {},
  onChanged: null,
  onOpenTask: null,
};

export default TaskDrawer;
